import { CommonModule, NgClass } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputMaskModule } from 'primeng/inputmask';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FormFieldComponent } from '../../../shared/components/form-field/form-field.component';
import { LayoutService } from '../../layout/service/app.layout.service';
import { I18nService } from '../../service/i18n.service';
import { NotificacaoService } from '../../service/notificacao.service';
import { ToolsService } from './../../service/tools.service';
import { LoginResponseModel } from './models/login-response.model';
import { LoginService } from './service/login.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule,
    NgClass,
    // PrimeNG
    ButtonModule,
    FloatLabelModule,
    InputMaskModule,
    InputTextModule,
    PasswordModule,
    SelectButtonModule,
    // Componentes customizados
    FormFieldComponent,
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  // ========================================
  // PROPRIEDADES
  // ========================================
  form: FormGroup = {} as FormGroup;
  loading = signal<boolean>(false);
  isBotaoSalvar = signal<boolean>(false);

  valueLogin: boolean = true; // true = telefone, false = email

  // Parâmetros da URL para confirmação de conta
  token: string = '';
  pessoaId: number = 0;

  // ========================================
  // INJEÇÃO DE DEPENDÊNCIAS
  // ========================================
  private formBuilder = inject(FormBuilder);
  private router = inject(Router);
  private activeRoute = inject(ActivatedRoute);
  private loginService = inject(LoginService);
  private notificacaoService = inject(NotificacaoService);
  private translate = inject(TranslateService);
  private i18nService = inject(I18nService);
  public layoutService = inject(LayoutService);

  // ========================================
  // LIFECYCLE HOOKS
  // ========================================
  ngOnInit(): void {
    this.translate.use(this.i18nService.idioma.value.code);
    this.criarFormulario();
    this.verificarParametrosUrl();
  }

  // ========================================
  // INICIALIZAÇÃO
  // ========================================
  private criarFormulario(): void {
    this.form = this.formBuilder.group({
      email: [null],
      telefone: [null, [Validators.required]], // Começa com telefone obrigatório
      senha: [null, [Validators.required]],
    });
  }

  private verificarParametrosUrl(): void {
    this.activeRoute.queryParamMap.subscribe((params) => {
      this.token = params.get('token') || '';
      this.pessoaId = +(params.get('id') || 0);

      if (this.token && this.pessoaId) {
        this.confirmarConta();
      }
    });
  }

  // ========================================
  // VALIDAÇÕES
  // ========================================
  validarClasseErro(campo: string): string {
    return this.validarCampo(campo) ? 'cs-input--error' : '';
  }

  validarCampo(campo: string): boolean {
    const control = this.form.get(campo);
    return !!(
      control?.errors &&
      (control?.dirty || control?.touched || this.isBotaoSalvar())
    );
  }

  private removerEspacos(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control instanceof FormControl && typeof control.value === 'string') {
        const trimmed = control.value.trim();
        control.setValue(trimmed, { emitEvent: false });
      }
    });
  }

  // ========================================
  // AUTENTICAÇÃO
  // ========================================
  login(): void {
    if (this.form.invalid) {
      this.isBotaoSalvar.set(true);
      this.form.markAllAsTouched();
      return;
    }

    this.loading.set(true);
    this.removerEspacos(this.form);

    // Prepara dados
    let telefone = this.form.value.telefone;
    if (telefone) {
      telefone = ToolsService.removerMascara(telefone);
    }

    // Faz login
    this.loginService
      .login(this.form.value.email, telefone, this.form.value.senha)
      .subscribe({
        next: (response: LoginResponseModel) => {
          this.loading.set(false);
          if (response) {
            this.router.navigate(['selecao-sistemas']);
          }
        },
        error: (error) => {
          this.loading.set(false);
          this.notificacaoService.notificacaoError(error.error.error.message);
        },
      });
  }

  private confirmarConta(): void {
    this.loginService.confirmarConta(this.token, this.pessoaId).subscribe({
      next: () => {
        this.notificacaoService.notificacaoSuccess(
          'Conta confirmada com sucesso!'
        );
      },
      error: (error) => {
        console.error('Erro ao confirmar conta:', error);
        this.notificacaoService.notificacaoError('Erro ao confirmar conta');
      },
    });
  }

  // ========================================
  // INTERFACE - TOGGLE DE LOGIN
  // ========================================
  alterarOpcaoLogin(useTelefone: boolean): void {
    if (this.valueLogin === useTelefone) return; // Não faz nada se já está no modo correto

    this.valueLogin = useTelefone;
    this.configurarValidacoes();
  }

  toggleLoginType(): void {
    this.valueLogin = !this.valueLogin;
    this.configurarValidacoes();
  }

  private configurarValidacoes(): void {
    const emailControl = this.form.get('email');
    const telefoneControl = this.form.get('telefone');

    // Limpa valores e erros
    emailControl?.setValue(null);
    telefoneControl?.setValue(null);
    emailControl?.setErrors(null);
    telefoneControl?.setErrors(null);

    if (this.valueLogin) {
      // Modo telefone
      emailControl?.clearValidators();
      telefoneControl?.setValidators([Validators.required]);
    } else {
      // Modo email
      telefoneControl?.clearValidators();
      emailControl?.setValidators([Validators.required, Validators.email]);
    }

    // Atualiza validações
    emailControl?.updateValueAndValidity();
    telefoneControl?.updateValueAndValidity();

    // Reset estado de validação
    this.isBotaoSalvar.set(false);
  }

  // ========================================
  // GETTERS
  // ========================================
  get isFormValid(): boolean {
    return this.form.valid;
  }

  getAlternativeButtonText(): string {
    const baseText = this.translate.instant('PAGINA_LOGIN.ENTRAR_COM');
    if (this.valueLogin) {
      return `${baseText} ${this.translate.instant(
        'CAMPO_ENTRADA.EMAIL.ROTULO'
      )}`;
    } else {
      return `${baseText} ${this.translate.instant(
        'CAMPO_ENTRADA.TELEFONE.ROTULO'
      )}`;
    }
  }
}
